/* eslint-disable @typescript-eslint/no-explicit-any */
import {DestroyRef, Injectable} from '@angular/core';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {NgEventBus} from 'ng-event-bus';
import {catchError, finalize, map, Observable, of, Subscription, tap} from 'rxjs';
import {MapToolsService} from '../../../../services/map-tools.service';
import {MapService} from '../../../../services/map.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {switchMap} from 'rxjs/operators';
import {SpatialSnapshotService} from '../../../../snapshot/spatial-snapshot.service';
import {AlertModalService} from '../../../../../common/component/modal';
import {LineService} from '../../../../services/features/line.service';
import {LineTypeLookup} from '../../../../../common/models/line-type.model';
import {LookupService} from '../../../../../common/service/lookup.service';
import {AnnotationService} from '../../../../services/features/annotation.service';
import {FeatureApplyDeleteService} from '../../../../services/feature-apply-delete.service';
import {AngleUtilService} from '../../../../services/features/angle/angle-util.service';
import {PolygonUtilService} from '../../../../services/features/polygon-util.service';
import {forkJoinDefaultIfEmpty} from '../../../../../common/observable.utils';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ToolServiceInterface} from './tool-service-interface';

@Injectable({
    providedIn: 'root'
})
export class MergePointsConstructionToolService implements ToolServiceInterface {
    title = 'MergePointsConstructionToolService';

    private selectedPoint1 = undefined;

    private selectedPoint2 = undefined;

    private lineGraphic = undefined;

    private pointSymbolSize = 14;

    private lineSymbolSize = 4;

    private lineSymbol: any;

    private removedSymbol: any;

    private retainedSymbol: any;

    private clearSubscription: Subscription;

    private closeSubscription: Subscription;

    private scaleMapEventSubscription: Subscription;

    private mapEvents: {unload?: any} = {};

    constructor(
        private angleUtilService: AngleUtilService,
        private alertModalService: AlertModalService,
        private annotationService: AnnotationService,
        private constructionToolModelService: ConstructionToolModelService,
        private featuresApplyDeleteService: FeatureApplyDeleteService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private lineService: LineService,
        private lookupService: LookupService,
        private mapService: MapService,
        private mapToolsService: MapToolsService,
        private polygonUtilService: PolygonUtilService,
        private spatialSnapshotService: SpatialSnapshotService,
        private spatialUtilService: SpatialUtilService,
        private destroyRef: DestroyRef
    ) {}

    setupFeatureEventModification() {
        console.debug(`${this.title} - setupFeatureEventModification`);

        const tool = this.getTool();

        tool.applyFeatureAction = () => this.reverse();

        tool.applyFeatureActionIsDisabled = () => !!tool.errors.length || !this.selectedPoint1 || !this.selectedPoint2;

        tool.createIsDisabled = () => !!tool.errors.length;

        if (!this.clearSubscription) {
            this.clearSubscription = this.eventBus
                .on('constructionToolService:mergePoints:clear')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.clear());
        }

        if (!this.closeSubscription) {
            this.closeSubscription = this.eventBus
                .on('constructionTool:mergePoints:close')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.clear();

                    if (this.clearSubscription) {
                        this.clearSubscription.unsubscribe();
                        this.clearSubscription = null;
                    }

                    if (this.closeSubscription) {
                        this.closeSubscription.unsubscribe();
                        this.closeSubscription = null;
                    }

                    if (this.scaleMapEventSubscription) {
                        this.scaleMapEventSubscription.unsubscribe();
                        this.scaleMapEventSubscription = null;
                    }
                });
        }

        if (!this.scaleMapEventSubscription) {
            this.scaleMapEventSubscription = this.eventBus
                .on('scaleMapEvent')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    if (this.selectedPoint1 && this.selectedPoint2 && this.lineGraphic) {
                        this.spatialUtilService.removeMapGraphic(this.selectedPoint1.graphic);
                        this.spatialUtilService.removeMapGraphic(this.selectedPoint2.graphic);
                        this.spatialUtilService.removeMapGraphic(this.lineGraphic);
                        this.spatialUtilService.addMapGraphic('line', this.lineGraphic);
                        this.spatialUtilService.addMapGraphic('point', this.selectedPoint1.graphic);
                        this.spatialUtilService.addMapGraphic('point', this.selectedPoint2.graphic);
                    }
                });
        }
    }

    create() {
        console.debug(`${this.title} - create`);

        return this.mapToolsService.activate(
            'CONSTRUCTION_TOOLS_MERGE_POINTS',
            () => {
                this.spatialUtilService.setMapCursor(this.spatialUtilService.getCrossCursorUrl());

                this.mapUnloadHandler();

                this.mapEvents.unload = this.mapEvents.unload || this.getMap().on('unload', () => this.mapUnloadHandler());

                this.setupTools();
            },
            () => {
                this.mapUnloadHandler();
                this.resetSelectedPoints();
                this.eventBus.cast('deactivateConstructionTools');
            }
        );
    }

    finish() {
        this.validate();
        if (this.getTool().errors.length) {
            return of(null);
        }

        return this.spatialSnapshotService.createSnapshot().pipe(
            switchMap(() => this.mergePoints()),
            tap(() => {
                // unload handlers and reset variables
                this.clear();

                // reset action behaviours - create button and map handlers
                this.eventBus.cast('resetActionBehaviours');
            }),
            catchError(err => {
                console.error(`${this.title} error creating snapshot:`, err);
                this.alertModalService.createAlertMessage('Error occurred creating snapshot', 'Ok', 'error');
                return err;
            }),
            finalize(() => this.mapToolsService.deactivate('CONSTRUCTION_TOOLS_MERGE_POINTS'))
        );
    }

    private getMap() {
        return this.spatialUtilService.getMap();
    }

    private mapUnloadHandler() {
        this.detachMapEvents();
    }

    private attachMapEventHandler(eventName: string, eventHandler: any) {
        this.mapEvents[eventName] = this.mapEvents[eventName] || this.getMap().on(eventName, eventHandler);
    }

    private detachMapEvents() {
        Object.keys(this.mapEvents).forEach((k: string) => {
            this.mapEvents[k].remove();
            delete this.mapEvents[k];
        });
    }

    private clear() {
        this.mapToolsService.deactivate('CONSTRUCTION_TOOLS_MERGE_POINTS');
    }

    private getTool() {
        return this.constructionToolModelService.getTool('mergePoints');
    }

    private getLineTypeLookups(): LineTypeLookup[] {
        return this.lookupService.getLookups('lineType');
    }

    private resetSelectedPoints() {
        if (this.selectedPoint1) {
            this.spatialUtilService.removeMapGraphic(this.selectedPoint1.graphic);
            this.selectedPoint1 = undefined;
        }

        if (this.selectedPoint2) {
            this.spatialUtilService.removeMapGraphic(this.selectedPoint2.graphic);
            this.selectedPoint2 = undefined;
        }

        if (this.lineGraphic) {
            this.spatialUtilService.removeMapGraphic(this.lineGraphic);
            this.lineGraphic = undefined;
        }
    }

    private validate() {
        console.debug(`${this.title} validate`);

        const tool = this.getTool();

        tool.errors.length = 0;
        tool.warnings.length = 0;
        tool.information.length = 0;

        if (!this.selectedPoint1 || !this.selectedPoint2) {
            tool.errors.push(tool.errorMessage1);
        }
    }

    /**
     * Reverses the selected points and associated line graphics.
     */
    private reverse() {
        if (!this.selectedPoint1 || !this.selectedPoint2) {
            return;
        }

        // clear graphics
        this.spatialUtilService.removeMapGraphic(this.selectedPoint1.graphic);
        this.spatialUtilService.removeMapGraphic(this.selectedPoint2.graphic);
        this.spatialUtilService.removeMapGraphic(this.lineGraphic);

        // swap selected points
        const newSelectedPoint1 = {
            graphic: new this.esriModuleService.Graphic(this.selectedPoint2.graphic.geometry, this.removedSymbol),
            featureType: this.selectedPoint2.featureType,
            feature: this.selectedPoint2.feature
        };

        const newSelectedPoint2 = {
            graphic: new this.esriModuleService.Graphic(this.selectedPoint1.graphic.geometry, this.retainedSymbol),
            featureType: this.selectedPoint1.featureType,
            feature: this.selectedPoint1.feature
        };

        this.selectedPoint1 = newSelectedPoint1;
        this.selectedPoint2 = newSelectedPoint2;

        this.lineGraphic = this.createLineGraphic(this.lineGraphic.spatialReference);

        if (this.selectedPoint1 && this.selectedPoint2 && this.lineGraphic) {
            this.spatialUtilService.addMapGraphic('line', this.lineGraphic);
            this.spatialUtilService.addMapGraphic('point', this.selectedPoint1.graphic);
            this.spatialUtilService.addMapGraphic('point', this.selectedPoint2.graphic);
        }
    }

    private mergePoints() {
        console.debug(`${this.title} - mergePoints`);

        const removedPoint = this.selectedPoint1.feature;
        const retainedPoint = this.selectedPoint2.feature;

        const linesToDelete: any[] = this.lineService.getLinesBetweenPoints(removedPoint, retainedPoint);

        const edits: {line?: any; polygon?: any; polyline?: any} = {};

        const linesToDeleteIds: number[] = linesToDelete.map(l => l.attributes.id);

        // Find all the angles formed from the 'single line connecting the two selected points together
        return this.deleteAssociatedAngles(removedPoint.attributes.id, linesToDeleteIds).pipe(
            // Find all the total lot boundaries formed from the 'single line connecting the two selected points together
            switchMap(() => this.updateAssociatedTotalLotBoundaries(linesToDelete, removedPoint, retainedPoint)),

            // Update all lines that reference the point to be deleted, including recalculate, reposition and rotate all annotation associated
            // with the lines that were connected to the point that was removed. Treat each annotation as though it was created for the first time.
            switchMap(() => this.updateAssociatedLines(removedPoint, retainedPoint)),
            tap(updates => (edits.line = {updates: updates})),

            // Update all polygons that reference any updated line
            switchMap(() => this.updateAssociatedPolygons(linesToDelete, edits.line.updates)),
            tap(polygonEdits => (edits.polygon = polygonEdits)),

            // Update associated polylines
            switchMap(() => this.updateAssociatedPolylines(removedPoint, retainedPoint)),
            tap(polylineEdits => (edits.polyline = polylineEdits)),

            // Update all lots that reference any updated polygon
            switchMap(() => this.updateAssociatedParentFeatures(this.spatialUtilService.getLotMapLayer(), {polygon: edits.polygon})),

            // Update all roads that reference any updated polygon
            switchMap(() => this.updateAssociatedParentFeatures(this.spatialUtilService.getRoadMapLayer(), {polygon: edits.polygon})),

            // Update associated interests
            switchMap(() => this.updateAssociatedInterestFeatures({polygon: edits.polygon, polyline: edits.polyline})),

            // Update Boundary and Corner Offset features that reference the deleted point
            switchMap(() => this.deleteAssociatedOffsetFeatures(removedPoint.attributes.id, linesToDeleteIds)),

            // Delete the 'single line connecting the two selected points together'
            switchMap(() => (linesToDelete.length ? this.deleteFeatures(this.spatialUtilService.getLineMapLayer(), linesToDelete) : of(null))),

            // Delete the 'Point' (ie the first point selected, which was marked to be removed)
            switchMap(() => this.deleteFeatures(this.spatialUtilService.getPointMapLayer(), [removedPoint])),

            // Delete invalid road annotations
            switchMap(() => this.spatialUtilService.deleteInvalidRoadAnnotations()),

            // Delete invalid interest annotations
            switchMap(() => this.spatialUtilService.deleteInvalidInterestAnnotations())
        );
    }

    private setupTools() {
        console.debug(this.title + ' setupTools');

        // clear selected point and line graphics
        this.resetSelectedPoints();

        // unhighlight any selected features
        this.spatialUtilService.unhighlightAllGeometryFeatures();

        // disable double click events on the map service
        this.mapService.map.disableDoubleClickZoom();

        // disable measurement tools and snapping tools
        this.mapService.disableMeasurementTool();
        this.mapService.disableSnappingTool();

        // enable snapping tool for merge points
        this.mapService.enableSnappingTool([this.spatialUtilService.getPointMapLayer(), this.spatialUtilService.getControlPointMapLayer()], {
            ctrlKeyDisablesSnapping: true,
            snapToEdge: false,
            snapToPoint: true,
            tolerance: 30
        });

        // set up the symbols to be used for point and line graphics
        this.setupSymbolsForGraphics();

        this.mapService.enableMeasurementTool('distance', {
            lineSymbol: this.lineSymbol,
            pointSymbol: new this.esriModuleService.SimpleMarkerSymbol(this.esriModuleService.SimpleMarkerSymbol.STYLE_NULL, 1)
        });

        this.setupMeasurementToolEvents();
        this.setupMapClickEvent();
    }

    private setupMeasurementToolEvents() {
        const logPrefix = `${this.title} - setupMeasurementToolEvents`;
        console.debug(logPrefix);

        this.mapService.measurementTool.on('measure', () => {
            if (this.mapService.measurementTool?._measureGraphic) {
                console.debug(this.title + ' on measure');

                window.setTimeout(() => {
                    if (this.selectedPoint1 && this.selectedPoint2) {
                        this.mapService.disableMeasurementTool();
                    }
                }, 10);
            }
        });

        this.mapService.measurementTool.on('measure-end', () => console.debug(`${logPrefix} - on measure-end`));
    }

    private setupMapClickEvent() {
        console.debug(`${this.title} - setupMapClickEvent`);

        this.detachMapEvents();

        this.attachMapEventHandler('click', (evt: any) => {
            const logPrefix = `${this.title} - on map click`;
            console.debug(logPrefix);

            if (this.mapService.snappingManagerTool) {
                this.mapService.snappingManagerTool.getSnappingPoint(evt.screenPoint).then((sp: any) => {
                    if (sp) {
                        if (this.selectedPoint1 && this.selectedPoint2) {
                            console.debug(`${logPrefix} - two points are already selected - no further action`);
                            return;
                        }

                        const controlPointFeature = this.spatialUtilService.findControlPointByCoords(sp.x, sp.y);

                        const pointFeature = this.spatialUtilService.findPointByCoords(sp.x, sp.y);

                        const selectedPoint = {
                            graphic: new this.esriModuleService.Graphic(sp, (this.selectedPoint1 && this.retainedSymbol) || this.removedSymbol),
                            featureType: (controlPointFeature && 'controlpoint') || 'point',
                            feature: controlPointFeature || pointFeature
                        };

                        if (!this.selectedPoint1) {
                            // No points selected
                            this.selectedPoint1 = selectedPoint;
                            this.spatialUtilService.addMapGraphic('point', this.selectedPoint1.graphic);
                        } else if (this.selectedPoint1.feature.attributes.id !== selectedPoint.feature.attributes.id) {
                            // First point already selected
                            this.selectedPoint2 = selectedPoint;
                            this.lineGraphic = this.createLineGraphic(sp.spatialReference);

                            // once the points and line graphics have been created then add them
                            // line goes underneath points
                            if (this.selectedPoint1 && this.selectedPoint2 && this.lineGraphic) {
                                this.spatialUtilService.removeMapGraphic(this.selectedPoint1.graphic);
                                this.spatialUtilService.addMapGraphic('line', this.lineGraphic);
                                this.spatialUtilService.addMapGraphic('point', this.selectedPoint1.graphic);
                                this.spatialUtilService.addMapGraphic('point', this.selectedPoint2.graphic);
                            }
                        } else {
                            // Reset when same point selected again
                            this.setupTools();
                        }
                    } else {
                        // Reset when snapping point not found
                        this.setupTools();
                    }
                });
            }

            this.eventBus.cast('mergePointClickCompleted');
        });

        this.attachMapEventHandler('dbl-click', () => {
            if (this.mapService.measurementTool) {
                console.debug(`${this.title} - on map dbl-click`);
                return false;
            }
            return undefined;
        });
    }

    private createLineGraphic(spatialReference: any) {
        const line = new this.esriModuleService.geometry.Polyline(spatialReference);

        const x1 = this.selectedPoint1.graphic.geometry.x;
        const y1 = this.selectedPoint1.graphic.geometry.y;
        const x2 = this.selectedPoint2.graphic.geometry.x;
        const y2 = this.selectedPoint2.graphic.geometry.y;

        line.addPath([
            [x1, y1],
            [x2, y2]
        ]);

        return new this.esriModuleService.Graphic(line, this.lineSymbol);
    }

    private setupSymbolsForGraphics() {
        this.removedSymbol = new this.esriModuleService.PictureMarkerSymbol({
            url: '/assets/cross-circle.svg',
            height: this.pointSymbolSize,
            width: this.pointSymbolSize,
            type: 'esriPMS'
        });

        this.retainedSymbol = new this.esriModuleService.PictureMarkerSymbol({
            url: '/assets/check-circle.svg',
            height: this.pointSymbolSize,
            width: this.pointSymbolSize,
            type: 'esriPMS'
        });

        // symbols for use in the measurement tool and line graphic
        this.lineSymbol = new this.esriModuleService.SimpleLineSymbol({
            style: this.esriModuleService.SimpleLineSymbol.STYLE_SOLID,
            color: new this.esriModuleService.Color([0, 112, 255, 0.9]),
            width: this.lineSymbolSize
        });
    }

    /**
     * Find all the angles that contains association with line parameter and then delete all these angles.
     */
    private deleteAssociatedAngles(pointId: number, lineIds: number[]) {
        const angles = this.spatialUtilService
            .getAngles()
            .filter(
                (f: any) =>
                    f.attributes.pointid === pointId || lineIds.includes(f.attributes.anglefromlineid) || lineIds.includes(f.attributes.angletolineid)
            );

        return angles.length ? this.deleteFeatures(this.spatialUtilService.getAngleMapLayer(), angles) : of(null);
    }

    /**
     * Find all the total lot boundaries that contain association with line parameter and then delete or update accordingly
     */
    private updateAssociatedTotalLotBoundaries(linesToDelete: any[], removedPoint: any, retainedPoint: any) {
        const linesToDeleteIds: number[] = linesToDelete.map(l => l.attributes.id);

        const totalLotBoundaryLookup = this.getLineTypeLookups().find(l => l.businessClass === 'TotalLotBoundary');

        const removedPointId = removedPoint.attributes.id;
        const retainedPointId = retainedPoint.attributes.id;

        const lines: any[] = this.spatialUtilService
            .getLines()
            .filter((l: any) =>
                l.attributes.lineids ? l.attributes.type === totalLotBoundaryLookup.id : l.attributes.frompointid && l.attributes.topointid
            );

        const tlbInfoList = lines
            .filter(l => l.attributes.lineids)
            .map(tlb => {
                const lineIds: number[] = this.spatialUtilService.parseIdsAttr(tlb.attributes.lineids);

                const pointIds = lineIds
                    .map(id => lines.find(l => l.attributes.id === id))
                    .reduce((acc, v, i, a) => {
                        const {frompointid: fromPointId, topointid: toPointId} = v.attributes;
                        if (i === 0) {
                            acc.push(
                                a[1] && [a[1].attributes.frompointid, a[1].attributes.topointid].includes(fromPointId) ? toPointId : fromPointId
                            );
                        }
                        acc.push(acc.includes(fromPointId) ? toPointId : fromPointId);
                        return acc;
                    }, []);

                return {tlb: tlb, lineIds: lineIds, pointIds: pointIds};
            })
            .filter(tlbInfo => tlbInfo.pointIds.includes(removedPointId) || tlbInfo.pointIds.includes(retainedPointId));

        const {deletes, updates} = tlbInfoList.reduce(
            (acc, tlbInfo) => {
                const tlb = tlbInfo.tlb;
                const oldPointIds = tlbInfo.pointIds;
                const oldLineIds = tlbInfo.lineIds;
                const newLineIds = oldLineIds.filter(id => !linesToDeleteIds.includes(id));

                // Delete total lot boundary if either of the following is true:
                // - Less than 2 lines will remain in the total lot boundary
                // - Total lot boundary includes the removed point but not retained point
                // - The total lot boundary contains both the removed and retained points, but they are not adjacent
                if (
                    newLineIds.length < 2 ||
                    (oldPointIds.includes(removedPointId) && !oldPointIds.includes(retainedPointId)) ||
                    (oldPointIds.includes(removedPointId) &&
                        oldPointIds.includes(retainedPointId) &&
                        Math.abs(oldPointIds.indexOf(removedPointId) - oldPointIds.indexOf(retainedPointId)) > 1)
                ) {
                    acc.deletes.push(this.spatialUtilService.toApplyEditsDelete('line', tlb));
                } else if (newLineIds.length < oldLineIds.length) {
                    const tlbUpdate = new this.esriModuleService.Graphic(tlb.toJson());
                    const firstOldLineId = oldLineIds[0];
                    if (newLineIds[0] !== firstOldLineId && tlbUpdate.attributes.frompointid !== retainedPointId) {
                        tlbUpdate.attributes.frompointid = retainedPointId;
                        tlbUpdate.geometry.paths[0][0] = [retainedPoint.geometry.x, retainedPoint.geometry.y];
                    }

                    const lastOldLineId = oldLineIds[oldLineIds.length - 1];
                    if (newLineIds[newLineIds.length - 1] !== lastOldLineId && tlbUpdate.attributes.topointid !== retainedPointId) {
                        tlbUpdate.attributes.topointid = retainedPointId;
                        tlbUpdate.geometry.paths[0][1] = [retainedPoint.geometry.x, retainedPoint.geometry.y];
                    }

                    tlbUpdate.attributes.distance -= linesToDelete
                        .map(l => l.attributes.distance)
                        .filter(d => d)
                        .reduce((acc, d) => acc + d, 0);

                    tlbUpdate.attributes.lineids = this.spatialUtilService.toIdsAttr(newLineIds);

                    acc.updates.push(tlbUpdate);
                }

                return acc;
            },
            {deletes: [], updates: []}
        );

        let observable: Observable<unknown> = deletes.length ? this.deleteFeatures(this.spatialUtilService.getLineMapLayer(), deletes) : of(null);

        if (updates.length) {
            observable = observable.pipe(
                switchMap(() => this.updateFeatures(this.spatialUtilService.getLineMapLayer(), updates)),
                switchMap(() => forkJoinDefaultIfEmpty(updates.map(update => this.annotationService.update(update, 'line', {updateGeometry: true}))))
            );
        }

        return observable;
    }

    /**
     * Find all construct point/line features that contain association with the deleted point as parameters and then delete the found offset features.
     */
    private deleteAssociatedOffsetFeatures(pointId: number, lineIds: number[]) {
        const constructLines = this.spatialUtilService
            .getConstructLines()
            .filter(
                (f: any) =>
                    parseInt(`${f.attributes.frompointid}`.match(/\d+/)[0]) === pointId ||
                    (f.attributes.lineid && lineIds.includes(f.attributes.lineid))
            );

        if (constructLines.length === 0) {
            return of(null);
        }

        const constructLineIds: number[] = constructLines.reduce((acc: any[], f: any) => {
            acc.push(f.attributes.id);
            return acc;
        }, []);

        // delete construct lines
        return this.deleteFeatures(this.spatialUtilService.getConstructLineMapLayer(), constructLines).pipe(
            switchMap(() => {
                // find associated construct points
                const constructPoints: any[] = this.spatialUtilService
                    .getConstructPoints()
                    .filter(
                        (f: any) =>
                            f.attributes.sourcelineids &&
                            constructLineIds.some(constructLineId => f.attributes.sourcelineids.includes(`[${constructLineId}]`))
                    );
                if (constructPoints.length === 0) {
                    return of(null);
                }

                // delete construct points
                return this.deleteFeatures(this.spatialUtilService.getConstructPointMapLayer(), constructPoints).pipe(
                    switchMap(() => {
                        // find additional construct lines associated with the construct points, filtering out construct lines that are already known
                        const additionalConstructLines = this.spatialUtilService
                            .getConstructLines()
                            .filter(
                                (f: any) =>
                                    !constructLineIds.includes(f.attributes.id) &&
                                    constructPoints.some(
                                        point =>
                                            (point.attributes.sourcelineids && point.attributes.sourcelineids.includes(`[${f.attributes.id}]`)) ||
                                            point.attributes.id === f.attributes.frompointid ||
                                            point.attributes.id === f.attributes.topointid
                                    )
                            );
                        if (additionalConstructLines.length === 0) {
                            return of(null);
                        }
                        return this.deleteFeatures(this.spatialUtilService.getConstructLineMapLayer(), additionalConstructLines);
                    })
                );
            })
        );
    }

    private deleteFeatures(layer: any, features: any[]) {
        const featureClass = this.spatialUtilService.getFeatureClass(layer);

        // find related annotations by featureid and featuretype
        const relatedAnnotations = this.spatialUtilService.getAnnotations().filter((a: any) => {
            const attrs = a.attributes;
            return features.some(
                f =>
                    (attrs.featuretype && attrs.featuretype.toLowerCase() === featureClass && attrs.featureid === f.attributes.id) ||
                    (attrs.childtype && attrs.childtype.toLowerCase() === featureClass && attrs.childid === f.attributes.id)
            );
        });

        const observable = relatedAnnotations.length
            ? this.applyDelete(this.spatialUtilService.getFeatureLayer('annotation'), relatedAnnotations)
            : of(null);

        // delete the features
        return observable.pipe(switchMap(() => this.applyDelete(layer, features)));
    }

    private applyDelete(layer: any, features: any[]) {
        return this.featuresApplyDeleteService.commitFeaturesDeletion(layer, features, false).pipe(
            tap((deletedFeatures: any[]) => {
                if (deletedFeatures.length) {
                    console.debug(`${this.title} - applyDelete - features have been successfully deleted!`);
                } else {
                    console.error(`${this.title} - applyDelete - error deleting features!`);
                    throw new Error('Error deleting features');
                }
            })
        );
    }

    private updateFeatures(featureLayer: any, features: any[]) {
        if (features.length === 0) {
            return of([]);
        }
        return this.spatialUtilService.applyEdits(featureLayer, null, features, null).pipe(map(() => features));
    }

    private updateAssociatedLines(removedPoint: any, retainedPoint: any) {
        const removedPointId = removedPoint.attributes.id;
        const retainedPointId = retainedPoint.attributes.id;

        const lines = this.spatialUtilService.getLines().filter((l: any) => {
            if (!l.attributes.frompointid || !l.attributes.topointid || l.attributes.lineids) {
                return false;
            }
            const linePointIds = [l.attributes.frompointid, l.attributes.topointid];
            return linePointIds.includes(removedPointId) && !linePointIds.every(lpi => lpi === removedPointId || lpi === retainedPointId);
        });

        if (lines.length === 0) {
            return of([]);
        }

        const updates: any[] = lines.map((l: any) => {
            const newLineAttrs = structuredClone(l.attributes);
            const lineGeometryJson = l.geometry.toJson();

            if (l.attributes.frompointid === removedPointId) {
                newLineAttrs.frompointid = retainedPointId;
                lineGeometryJson.paths[0][0][0] = retainedPoint.geometry.x;
                lineGeometryJson.paths[0][0][1] = retainedPoint.geometry.y;
            } else {
                newLineAttrs.topointid = retainedPointId;
                lineGeometryJson.paths[0][1][0] = retainedPoint.geometry.x;
                lineGeometryJson.paths[0][1][1] = retainedPoint.geometry.y;
            }

            newLineAttrs.bearing = this.angleUtilService.ddToDms(
                this.lineService.calculateBearing(
                    lineGeometryJson.paths[0][0][0],
                    lineGeometryJson.paths[0][0][1],
                    lineGeometryJson.paths[0][1][0],
                    lineGeometryJson.paths[0][1][1]
                )
            );

            return new this.esriModuleService.Graphic({attributes: newLineAttrs, geometry: lineGeometryJson});
        });

        const projectedGeometries = this.esriModuleService.projection.project(
            updates.map(p => p.geometry),
            new this.esriModuleService.SpatialReference({wkid: 7844})
        );
        const lineLengths: any[] = this.esriModuleService.geodesicUtils.geodesicLengths(projectedGeometries, this.esriModuleService.units.METERS);
        lineLengths.forEach((v, i) => (updates[i].attributes.distance = updates[i].attributes.distance || parseFloat(v.toFixed(3))));

        return this.updateFeatures(this.spatialUtilService.getLineMapLayer(), updates).pipe(
            switchMap(() =>
                forkJoinDefaultIfEmpty(updates.map(lineUpdate => this.annotationService.update(lineUpdate, 'line', {updateGeometry: true})))
            ),
            map(() => updates)
        );
    }

    private updateAssociatedPolylines(removedPoint: any, retainedPoint: any) {
        const removedPointId = removedPoint.attributes.id;
        const retainedPointId = retainedPoint.attributes.id;

        const polylines: any[] = this.spatialUtilService
            .getPolylines()
            .filter(polyline => this.spatialUtilService.parseIdsAttr(polyline.attributes.pointids).includes(removedPointId));

        const edits = polylines.reduce(
            (accEdits, polyline) => {
                const newAttrs = structuredClone(polyline.attributes);
                const geometryJson = polyline.geometry.toJson();

                const pointInfo = this.spatialUtilService.parseIdsAttr(polyline.attributes.pointids).reduce(
                    (acc: any, pointId: number, pointIdx: number, pointIds: number[]) => {
                        if (pointId === removedPointId) {
                            if (pointIdx === 0 || ![removedPointId, retainedPointId].includes(pointIds[pointIdx - 1])) {
                                acc.pointIds.push(retainedPointId);
                                acc.path.push([retainedPoint.geometry.x, retainedPoint.geometry.y]);
                            }
                        } else if (
                            pointIdx === 0 ||
                            !(pointId === retainedPointId && [removedPointId, retainedPointId].includes(pointIds[pointIdx - 1]))
                        ) {
                            acc.pointIds.push(pointId);
                            acc.path.push(geometryJson.paths[0][pointIdx]);
                        }
                        return acc;
                    },
                    {pointIds: [], path: []}
                );

                if (pointInfo.pointIds.length >= 2) {
                    newAttrs.pointids = this.spatialUtilService.toIdsAttr(pointInfo.pointIds);
                    geometryJson.paths[0] = pointInfo.path;
                    accEdits.updates.push(new this.esriModuleService.Graphic({attributes: newAttrs, geometry: geometryJson}));
                } else {
                    accEdits.deletes.push(this.spatialUtilService.toApplyEditsDelete('polyline', polyline));
                }

                return accEdits;
            },
            {updates: [], deletes: []}
        );

        let observable = edits.deletes.length ? this.deleteFeatures(this.spatialUtilService.getPolylineMapLayer(), edits.deletes) : of(null);

        if (edits.updates.length) {
            const projectedGeometries = this.esriModuleService.projection.project(
                edits.updates.map((p: any) => p.geometry),
                new this.esriModuleService.SpatialReference({wkid: 7844})
            );
            this.esriModuleService.geodesicUtils
                .geodesicLengths(projectedGeometries, this.esriModuleService.units.METERS)
                .forEach((length: number, lengthIdx: number) => (edits.updates[lengthIdx].attributes.distance = parseFloat(length.toFixed(3))));

            observable = observable.pipe(switchMap(() => this.updateFeatures(this.spatialUtilService.getPolylineMapLayer(), edits.updates)));
        }

        return observable.pipe(map(() => edits));
    }

    private updateAssociatedPolygons(linesToDelete: any[], updatedLines: any[]) {
        const linesToDeleteIds: number[] = linesToDelete.map(l => l.attributes.id);
        const updatedLinesIds: number[] = updatedLines.map(l => l.attributes.id);
        const allLineIds: number[] = [...linesToDeleteIds, ...updatedLinesIds];

        const lines: any[] = this.spatialUtilService
            .getLines()
            .filter((l: any) => !linesToDeleteIds.includes(l.attributes.id))
            .map((l: any) => (updatedLinesIds.includes(l.attributes.id) ? updatedLines[updatedLinesIds.indexOf(l.attributes.id)] : l));

        const polygons: any[] = this.spatialUtilService
            .getPolygons()
            .filter((p: any) => p.attributes.lineids && allLineIds.some(lineId => p.attributes.lineids.includes(`[${lineId}]`)));

        const edits = polygons.reduce(
            (acc, p) => {
                const linesToKeepIds: number[] = this.spatialUtilService
                    .parseIdsAttr(p.attributes.lineids)
                    .filter((id: number) => !linesToDeleteIds.includes(id));
                const linesToKeep: any[] = lines.filter(l => linesToKeepIds.includes(l.attributes.id));
                const polygonRingInfo = this.polygonUtilService.getPolygonRing(linesToKeep, undefined);

                if (polygonRingInfo.ring && polygonRingInfo.ring.length > 3) {
                    const polygonGeometryJson = p.geometry.toJson();
                    polygonGeometryJson.rings[0] = polygonRingInfo.ring.map(point => [point.x, point.y]);

                    const newPolygonAttrs = Object.assign(structuredClone(p.attributes), {lineids: polygonRingInfo.lineids});
                    acc.updates.push(new this.esriModuleService.Graphic({attributes: newPolygonAttrs, geometry: polygonGeometryJson}));
                } else {
                    acc.deletes.push(this.spatialUtilService.toApplyEditsDelete('polygon', p));
                }

                return acc;
            },
            {updates: [], deletes: []}
        );

        let observable: any = edits.deletes.length ? this.deleteFeatures(this.spatialUtilService.getPolygonMapLayer(), edits.deletes) : of(null);

        if (edits.updates.length) {
            const polygonAreas = this.spatialUtilService.geodesicAreas(edits.updates.map((p: any) => p.geometry));
            polygonAreas.forEach(
                (area: number, areaIndex: number) =>
                    (edits.updates[areaIndex].attributes.statedarea = edits.updates[areaIndex].attributes.statedarea || `${Math.round(area)}`)
            );
            observable = observable.pipe(
                switchMap(() => this.updateFeatures(this.spatialUtilService.getPolygonMapLayer(), edits.updates)),
                switchMap(() =>
                    forkJoinDefaultIfEmpty(edits.updates.map((u: any) => this.annotationService.update(u, 'polygon', {updateGeometry: false})))
                )
            );
        }

        return observable.pipe(map(() => edits));
    }

    private updateAssociatedInterestFeatures(childEdits: any) {
        const featureLayer = this.spatialUtilService.getInterestMapLayer();

        const polygonUpdates: any[] = childEdits.polygon?.updates.map((u: any) => new this.esriModuleService.Graphic(u.toJson())) || [];
        const polygonDeletes: any[] = childEdits.polygon?.deletes || [];
        const polygonUpdatesIds: number[] = polygonUpdates.map(p => p.attributes.id);
        const polygonDeletesIds: number[] = polygonDeletes.map(p => p.attributes.id);
        const polygonEditsIds: number[] = [...polygonUpdatesIds, ...polygonDeletesIds];

        const polylineUpdates: any[] = childEdits.polyline?.updates.map((u: any) => new this.esriModuleService.Graphic(u.toJson())) || [];
        const polylineDeletes: any[] = childEdits.polyline?.deletes || [];
        const polylineUpdatesIds: number[] = polylineUpdates.map(p => p.attributes.id);
        const polylineDeletesIds: number[] = polylineDeletes.map(p => p.attributes.id);
        const polylineEditsIds: number[] = [...polylineUpdatesIds, ...polylineDeletesIds];

        // Get lots associated with polygons
        const lotsWithEditedPolygons: any[] = this.spatialUtilService
            .getLots()
            .filter((f: any) => f.attributes.polygonids && polygonEditsIds.some(id => f.attributes.polygonids.includes(`[${id}]`)));
        const polygonLotInfo = {
            allBurdenedLotIds: lotsWithEditedPolygons
                .map(lot => lot.attributes.name)
                .filter(v => v)
                .map(v => parseInt(v)),
            allBurdenedLotPolygonIds: lotsWithEditedPolygons.reduce((acc, lot) => {
                acc.push(
                    ...this.spatialUtilService
                        .parseIdsAttr(lot.attributes.polygonids)
                        .filter((id: number) => !acc.includes(id) && !polygonDeletesIds.includes(id))
                );
                return acc;
            }, []),
            burdenedLotPolygonIdsMap: lotsWithEditedPolygons.reduce((acc, lot) => {
                if (lot.attributes.name) {
                    acc[parseInt(lot.attributes.name)] = this.spatialUtilService
                        .parseIdsAttr(lot.attributes.polygonids)
                        .filter((id: number) => !polygonDeletesIds.includes(id));
                }
                return acc;
            }, {})
        };
        const features = this.spatialUtilService
            .getGraphics(featureLayer)
            .filter(
                (f: any) =>
                    (f.attributes.polygonids && polygonEditsIds.some(id => f.attributes.polygonids.includes(`[${id}]`))) ||
                    (f.attributes.polylineids && polylineEditsIds.some(id => f.attributes.polylineids.includes(`[${id}]`))) ||
                    (f.attributes.burdenlots && polygonLotInfo.allBurdenedLotIds.some(id => f.attributes.burdenlots.match(`\\b${id}\\b`)))
            );

        if (features.length === 0) {
            return of([]);
        }

        const lotsWithBurdenLots: any[] = this.spatialUtilService
            .getLots()
            .filter(
                (lot: any) =>
                    lot.attributes.polygonids &&
                    features.some((f: any) => f.attributes.burdenlots && f.attributes.burdenlots.match(`\\b${lot.attributes.name}\\b`))
            );
        const interestLotInfo = {
            allBurdenedLotIds: lotsWithBurdenLots
                .map(lot => lot.attributes.name)
                .filter(v => v)
                .map(v => parseInt(v)),
            allBurdenedLotPolygonIds: lotsWithBurdenLots.reduce((acc, lot) => {
                acc.push(
                    ...this.spatialUtilService
                        .parseIdsAttr(lot.attributes.polygonids)
                        .filter((id: number) => !acc.includes(id) && !polygonDeletesIds.includes(id))
                );
                return acc;
            }, []),
            burdenedLotPolygonIdsMap: lotsWithBurdenLots.reduce((acc, lot) => {
                if (lot.attributes.name) {
                    acc[parseInt(lot.attributes.name)] = this.spatialUtilService
                        .parseIdsAttr(lot.attributes.polygonids)
                        .filter((id: number) => !polygonDeletesIds.includes(id));
                }
                return acc;
            }, {})
        };

        const featureChildIdsToKeepList = features.map((f: any) => ({
            feature: f,
            polygonIds: this.spatialUtilService.parseIdsAttr(f.attributes.polygonids).filter((id: number) => !polygonDeletesIds.includes(id)),
            polylineIds: this.spatialUtilService.parseIdsAttr(f.attributes.polylineids).filter((id: number) => !polylineDeletesIds.includes(id))
        }));

        const {polygonIdsToKeep, polylineIdsToKeep} = featureChildIdsToKeepList.reduce(
            (acc: any, v: any) => {
                acc.polygonIdsToKeep.push(...v.polygonIds.filter((id: number) => !acc.polygonIdsToKeep.includes(id)));
                acc.polylineIdsToKeep.push(...v.polylineIds.filter((id: number) => !acc.polylineIdsToKeep.includes(id)));
                return acc;
            },
            {polygonIdsToKeep: [], polylineIdsToKeep: []}
        );

        const getPolygonsResponse = polygonIdsToKeep.length
            ? this.spatialUtilService
                  .getPolygons()
                  .filter(
                      (p: any) => polygonIdsToKeep.includes(p.attributes.id) || interestLotInfo.allBurdenedLotPolygonIds.includes(p.attributes.id)
                  )
            : [];
        const getPolylinesResponse = polylineIdsToKeep.length
            ? this.spatialUtilService.getPolylines().filter(p => polylineIdsToKeep.includes(p.attributes.id))
            : [];

        // Replace updated polygons
        const polygons = polygonUpdates.concat(
            getPolygonsResponse
                .filter((p: any) => !polygonUpdatesIds.includes(p.attributes.id))
                .map((p: any) => new this.esriModuleService.Graphic(p.toJson()))
        );
        const polygonMap = polygons.reduce((acc: any, v: any) => {
            acc[v.attributes.id] = v;
            return acc;
        }, {});

        // Replace updated polylines
        const polylines = polylineUpdates.concat(
            getPolylinesResponse.filter(p => !polylineUpdatesIds.includes(p.attributes.id)).map(p => new this.esriModuleService.Graphic(p.toJson()))
        );
        const polylineMap = polylines.reduce((acc, v) => {
            acc[v.attributes.id] = v;
            return acc;
        }, {});

        const featureMap = featureChildIdsToKeepList.reduce((acc: any, v: any) => {
            const burdenedLotPolygonIds = this.spatialUtilService
                .parseIdsAttr(v.feature.attributes.burdenlots)
                .reduce((accBurdenedLotPolygonIds: number[], burdenedLotId: number) => {
                    if (interestLotInfo.burdenedLotPolygonIdsMap[burdenedLotId]) {
                        accBurdenedLotPolygonIds.push(
                            ...interestLotInfo.burdenedLotPolygonIdsMap[burdenedLotId].filter((id: number) => !accBurdenedLotPolygonIds.includes(id))
                        );
                    }
                    return accBurdenedLotPolygonIds;
                }, []);

            acc[v.feature.attributes.id] = {
                feature: v.feature,
                polygons: v.polygonIds.map((id: number) => polygonMap[id]),
                polylines: v.polylineIds.map((id: number) => polylineMap[id]),
                burdenedLotPolygons: burdenedLotPolygonIds.map((id: number) => polygonMap[id])
            };
            return acc;
        }, {});

        const updates = featureChildIdsToKeepList.map((v: any) => {
            const burdenedLotPolygons: any[] = featureMap[v.feature.attributes.id].burdenedLotPolygons;
            const burdenedLotPolygonRings = burdenedLotPolygons.map(burdenedLotPolygon => burdenedLotPolygon.geometry.rings[0]);

            const newFeatureAttrs = structuredClone(v.feature.attributes);
            newFeatureAttrs.polygonids = this.spatialUtilService.toIdsAttr(v.polygonIds);
            newFeatureAttrs.polylineids = this.spatialUtilService.toIdsAttr(v.polylineIds);

            let featureGeometryJson: any;
            if (v.feature.geometry && burdenedLotPolygonRings.length) {
                featureGeometryJson = v.feature.geometry;
                featureGeometryJson.rings.splice(0, featureGeometryJson.rings.length, ...burdenedLotPolygonRings);
            }

            return new this.esriModuleService.Graphic({attributes: newFeatureAttrs, geometry: featureGeometryJson || null});
        });

        return this.updateFeatures(featureLayer, updates);
    }

    private updateAssociatedParentFeatures(featureLayer: any, childEdits: any) {
        const fieldNames = featureLayer.fields.map((field: any) => field.name);
        const featureClass = this.spatialUtilService.getFeatureClass(featureLayer);

        const polygonUpdates: any[] = childEdits.polygon?.updates.map((u: any) => new this.esriModuleService.Graphic(u.toJson())) || [];
        const polygonDeletes: any[] = childEdits.polygon?.deletes || [];
        const polygonUpdatesIds: number[] = polygonUpdates.map(p => p.attributes.id);
        const polygonDeletesIds: number[] = polygonDeletes.map(p => p.attributes.id);
        const polygonEditsIds: number[] = [...polygonUpdatesIds, ...polygonDeletesIds];

        const polylineUpdates: any[] = childEdits.polyline?.updates.map((u: any) => new this.esriModuleService.Graphic(u.toJson())) || [];
        const polylineDeletes: any[] = childEdits.polyline?.deletes || [];
        const polylineUpdatesIds: number[] = polylineUpdates.map(p => p.attributes.id);
        const polylineDeletesIds: number[] = polylineDeletes.map(p => p.attributes.id);
        const polylineEditsIds: number[] = [...polylineUpdatesIds, ...polylineDeletesIds];

        const features = this.spatialUtilService
            .getGraphics(featureLayer)
            .filter(
                (f: any) =>
                    (f.attributes.polygonids && polygonEditsIds.some(id => f.attributes.polygonids.includes(`[${id}]`))) ||
                    (f.attributes.polylineids && polylineEditsIds.some(id => f.attributes.polylineids.includes(`[${id}]`)))
            );

        if (features.length === 0) {
            return of([]);
        }

        const featureChildIdsToKeepList = features.map((f: any) => ({
            feature: f,
            polygonIds: this.spatialUtilService.parseIdsAttr(f.attributes.polygonids).filter((id: number) => !polygonDeletesIds.includes(id)),
            polylineIds: this.spatialUtilService.parseIdsAttr(f.attributes.polylineids).filter((id: number) => !polylineDeletesIds.includes(id))
        }));

        const {polygonIdsToKeep, polylineIdsToKeep} = featureChildIdsToKeepList.reduce(
            (acc: any, v: any) => {
                acc.polygonIdsToKeep.push(...v.polygonIds.filter((id: number) => !acc.polygonIdsToKeep.includes(id)));
                acc.polylineIdsToKeep.push(...v.polylineIds.filter((id: number) => !acc.polylineIdsToKeep.includes(id)));
                return acc;
            },
            {polygonIdsToKeep: [], polylineIdsToKeep: []}
        );

        const getPolygonsResponse: any[] = polygonIdsToKeep.length
            ? this.spatialUtilService.getPolygons().filter((p: any) => polygonIdsToKeep.includes(p.attributes.id))
            : [];
        const getPolylinesResponse: any[] = polylineIdsToKeep.length
            ? this.spatialUtilService.getPolylines().filter(p => polylineIdsToKeep.includes(p.attributes.id))
            : [];

        // Replace updated polygons
        const polygons: any[] = polygonUpdates.concat(
            getPolygonsResponse
                .filter((p: any) => !polygonUpdatesIds.includes(p.attributes.id))
                .map((p: any) => new this.esriModuleService.Graphic(p.toJson()))
        );
        const polygonMap = polygons.reduce((acc, v) => {
            acc[v.attributes.id] = v;
            return acc;
        }, {});

        // Replace updated polylines
        const polylines = polylineUpdates.concat(
            getPolylinesResponse.filter(p => !polylineUpdatesIds.includes(p.attributes.id)).map(p => new this.esriModuleService.Graphic(p.toJson()))
        );
        const polylineMap = polylines.reduce((acc, v) => {
            acc[v.attributes.id] = v;
            return acc;
        }, {});

        const featureMap = featureChildIdsToKeepList.reduce((acc: any, v: any) => {
            acc[v.feature.attributes.id] = {
                feature: v.feature,
                polygons: v.polygonIds.map((id: number) => polygonMap[id]),
                polylines: v.polylineIds.map((id: number) => polylineMap[id])
            };
            return acc;
        }, {});

        const updates = featureChildIdsToKeepList.map((v: any) => {
            const featurePolygons: any[] = featureMap[v.feature.attributes.id].polygons;
            const featurePolygonRings = featurePolygons.map(featurePolygon => featurePolygon.geometry.rings[0]);

            const newFeatureAttrs = structuredClone(v.feature.attributes);

            if (fieldNames.includes('polygonids')) {
                newFeatureAttrs.polygonids = this.spatialUtilService.toIdsAttr(v.polygonIds);
            }

            if (fieldNames.includes('totalarea')) {
                newFeatureAttrs.totalarea = featurePolygons.reduce((acc, p) => acc + parseFloat(p.attributes.statedarea), 0);
            }

            if (fieldNames.includes('polylineids')) {
                newFeatureAttrs.polylineids = this.spatialUtilService.toIdsAttr(v.polylineIds);
            }

            let featureGeometryJson: any;
            if (newFeatureAttrs.polygonids) {
                if (featurePolygonRings.length === 0) {
                    featureGeometryJson = v.feature.geometry;
                    featureGeometryJson.rings.length = 0;
                } else {
                    featureGeometryJson = this.spatialUtilService
                        .polygonGeometryUnion(
                            featurePolygonRings.map(ring => {
                                const geometry = v.feature.geometry.toJson();
                                geometry.rings.splice(0, geometry.rings.length, ring);
                                return new this.esriModuleService.geometry.Polygon(geometry);
                            })
                        )
                        .toJson();
                }
            }

            return new this.esriModuleService.Graphic({attributes: newFeatureAttrs, geometry: featureGeometryJson || null});
        });

        let observable: any = this.updateFeatures(featureLayer, updates);

        if (polygonEditsIds.length) {
            observable = observable.pipe(
                switchMap(() =>
                    forkJoinDefaultIfEmpty(
                        updates.map((u: any) => {
                            const featurePolygons = featureMap[u.attributes.id].polygons;
                            if (featurePolygons.length === 0) {
                                return of(null);
                            }

                            return this.annotationService.update(u, featureClass, {polygons: featurePolygons, updateGeometry: false}).pipe(
                                switchMap(() =>
                                    forkJoinDefaultIfEmpty(
                                        featurePolygons.map((p: any) => {
                                            u.attributes['polygon.statedarea'] = p.attributes.statedarea;
                                            p.attributes['polygon.statedarea'] = p.attributes.statedarea;
                                            return this.annotationService.update(p, 'polygon', {
                                                polygons: featurePolygons,
                                                childPolygon: p,
                                                updateGeometry: false
                                            });
                                        })
                                    )
                                )
                            );
                        })
                    )
                )
            );
        }

        return observable;
    }
}
